/**
 * Questo file definisce il componente ProtectedRoute.
 * - Funziona come un "guardiano" per le rotte che richiedono l'autenticazione dell'utente.
 * - Controlla lo stato di autenticazione e di caricamento tramite Redux.
 * - Se l'utente non è autenticato, lo reindirizza alla pagina di login.
 * - Mentre verifica lo stato, mostra un indicatore di caricamento.
 * - Se l'utente è autenticato, permette l'accesso alla rotta richiesta.
 * - Salva la posizione originale richiesta, per poter reindirizzare l'utente lì dopo il login.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Componente ProtectedRoute.
 * - Avvolge le rotte che necessitano di un utente loggato.
 *
 * @param {object} props - Le props del componente.
 * @param {React.ReactNode} props.children - Il componente React da renderizzare se l'autenticazione ha successo.
 * @returns {React.ReactElement} - Un elemento React: o il componente figlio, o un reindirizzamento, o un loader.
 */
const ProtectedRoute = ({ children }) => {
    // Accede allo stato di autenticazione dal Redux store.
    // 'isAuthenticated' indica se l'utente è loggato.
    // 'loading' indica se una richiesta di autenticazione (es. checkAuthStatus) è in corso.
    const { isAuthenticated, loading } = useSelector((state) => state.auth);

    // Ottiene la posizione corrente nella navigazione.
    // Questo è utile per reindirizzare l'utente alla pagina che stava tentando di visitare
    // prima di essere inviato al login.
    const location = useLocation();

    // Gestione dello stato di caricamento.
    // Se l'app sta ancora verificando se l'utente è autenticato (es. al primo caricamento),
    // mostra un indicatore di caricamento a schermo intero per evitare "sfarfallii" dell'interfaccia.
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Controllo dell'autenticazione.
    // Se l'utente NON è autenticato, viene reindirizzato alla pagina di login.
    if (!isAuthenticated) {
        // 'Navigate' è il componente di React Router per il reindirizzamento.
        // 'to="/login"' specifica la destinazione.
        // 'replace' sostituisce la voce corrente nella cronologia, così l'utente non può tornare alla pagina protetta con il tasto "indietro".
        // 'state={{ from: location }}' passa la posizione originale come stato, in modo che la pagina di login possa usarla per il reindirizzamento post-accesso.
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Se l'utente è autenticato, renderizza il componente figlio (la pagina protetta).
    return children;
};

// Esporta il componente per l'uso nel file di routing principale (App.js).
export default ProtectedRoute;